import "whatwg-fetch"; // polyfill for older browsers
import {
  getAPIConfig,
  convertQueryObjToString,
  handleAPIResponse,
} from "@utils/browser-helpers";
import { executeAPIRequest } from "@adapters/api";
import type { FilesAdapter } from "@types";

declare global {
  interface Window {
    commercialFmsAdapter: FilesAdapter;
  }
}

const commercialFmsAdapter: FilesAdapter = {
  fetchAllFiles: async (fetchConfig?: any): Promise<any> => {
    let route = `/commercial/${localStorage.ctid}/files${
      fetchConfig ? `${convertQueryObjToString(fetchConfig, true)}` : ""
    }`;
    const apiConfig = getAPIConfig(route, false);
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  fetchTrashFiles: async (fetchConfig?: any): Promise<any> => {
    let route = `/commercial/${localStorage.ctid}/files/trash${
      fetchConfig ? `${convertQueryObjToString(fetchConfig, true)}` : ""
    }`;
    const apiConfig = getAPIConfig(route, false);
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  fetchIndividualFile: async (fileId: number | string): Promise<any> => {
    let route = `/commercial/${localStorage.ctid}/files/id/${fileId}`;
    const apiConfig = getAPIConfig(route, false);
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  postNewFile: async (location: string, changes: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/file_upload/${location}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "POST", {}, changes);
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  deleteFile: async (fileId: number | string): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/id/${fileId}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "DELETE");
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  patchFile: async (fileId: number | string, changes: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/id/${fileId}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "PATCH", {}, changes);
    const responseData = await response.json();
    return handleAPIResponse(responseData, responseData);
  },
  clearFileCache: async (fileLocation: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/invalidate_cache/`,
      false
    );
    const response = await executeAPIRequest(
      apiConfig,
      "POST",
      {},
      fileLocation
    );
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  fetchIndividualFileLocation: async (fileLocationId: string): Promise<any> => {
    let route = `/commercial/${localStorage.ctid}/files/file_location/${fileLocationId}`;
    const apiConfig = getAPIConfig(route, false);
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  postNewFileLocation: async (fileLocation: string, changes: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/file_location/${fileLocation}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "POST", {}, changes);
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  deleteFileLocation: async (fileLocation: string): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/file_location/${fileLocation}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "DELETE", {}, {});
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  restoreFile: async (fileID: number | string, changes: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/trash/${fileID}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "PATCH", {}, changes);
    const responseData = await response.json();
    return handleAPIResponse(responseData, responseData);
  },

  fetchCTIDAssets: async (ctid: string | number): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/import_files?source_ctid=${ctid}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData, responseData);
  },

  importFiles: async (changes: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/import_files`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "POST", {}, changes);
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  syncFiles: async (changes: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/import_files`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "PATCH", {}, changes);
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },
  fetchAssetsVersions: async (filePath: string): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/file_versions?keyname=${filePath}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData, responseData);
  },
  revertAssetsVersion: async (payload: any): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/file_versions`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "POST", {}, payload);
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },

  fetchFileBucketLocation: async (fileLocation: string, title: string): Promise<any> => {
    const apiConfig = getAPIConfig(
      `/commercial/${localStorage.ctid}/files/file_upload/${fileLocation}?title=${title}`,
      false
    );
    const response = await executeAPIRequest(apiConfig, "GET");
    const responseData = await response.json();
    return handleAPIResponse(responseData);
  },
};

window.commercialFmsAdapter = commercialFmsAdapter;

export default commercialFmsAdapter;
